import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "1. Website Purpose",
    blocks: [
      {
        type: "p",
        text: "The iVIBES Consulting website provides general information about our consulting services, capabilities, business philosophy, and potential engagement opportunities.",
      },
      {
        type: "p",
        text: "Information presented on this website is for general informational purposes and does not constitute a binding proposal, contract, guarantee, or professional engagement.",
      },
    ],
  },
  {
    title: "2. Discovery Calls",
    blocks: [
      {
        type: "p",
        text: "Visitors may schedule a discovery call to discuss their business needs and determine whether there may be an appropriate fit with iVIBES Consulting.",
      },
      { type: "p", text: "Scheduling or participating in a discovery call does not by itself:" },
      {
        type: "ul",
        items: [
          "Create a client-consultant relationship",
          "Require either party to enter into an engagement",
          "Guarantee that iVIBES Consulting will accept an engagement",
          "Establish pricing, scope, timelines, or deliverables",
        ],
      },
      {
        type: "p",
        text: "Any paid consulting engagement will be governed by a separate written agreement, proposal, statement of work, or other mutually accepted document.",
      },
    ],
  },
  {
    title: "3. No Guarantee of Business Results",
    blocks: [
      {
        type: "p",
        text: "iVIBES Consulting helps organizations improve business systems, processes, technology use, customer acquisition, automation, and related areas.",
      },
      { type: "p", text: "Business outcomes depend on many factors outside our control." },
      { type: "p", text: "Nothing on this website constitutes a guarantee of:" },
      {
        type: "ul",
        items: [
          "Revenue",
          "Profit",
          "Lead volume",
          "Conversion rates",
          "Customer acquisition",
          "Cost savings",
          "Productivity improvements",
          "Advertising performance",
          "Technology performance",
          "Any other specific business result",
        ],
      },
      {
        type: "p",
        text: "Examples, demonstrations, or hypothetical scenarios presented on the website are intended to illustrate concepts and capabilities unless clearly identified as actual client results.",
      },
    ],
  },
  {
    title: "4. Live Demonstrations and Examples",
    blocks: [
      {
        type: "p",
        text: "The website may contain simulated demonstrations showing how technologies or business processes could operate.",
      },
      { type: "p", text: "These demonstrations are illustrative." },
      {
        type: "p",
        text: "Example companies, customers, conversations, appointments, names, or scenarios used in demonstrations are fictional unless expressly identified otherwise.",
      },
      {
        type: "p",
        text: "A demonstration should not be interpreted as evidence that a particular result is guaranteed for a prospective client.",
      },
    ],
  },
  {
    title: "5. Intellectual Property",
    blocks: [
      {
        type: "p",
        text: "Unless otherwise stated, website content owned by iVIBES Consulting—including text, graphics, branding, methodologies, designs, and original materials—is protected by applicable intellectual-property laws.",
      },
      {
        type: "p",
        text: "You may view and use the website for legitimate personal or business-evaluation purposes.",
      },
      {
        type: "p",
        text: "You may not reproduce, publish, distribute, sell, modify, or commercially exploit proprietary website content without permission from iVIBES Consulting, except as permitted by law.",
      },
    ],
  },
  {
    title: "6. Trademarks and Brand Names",
    blocks: [
      {
        type: "p",
        text: "Names, logos, service names, methodologies, slogans, and other brand identifiers used by iVIBES Consulting may constitute trademarks or proprietary branding.",
      },
      {
        type: "p",
        text: "Reference to third-party products, companies, platforms, or services does not imply endorsement, sponsorship, or affiliation unless expressly stated.",
      },
    ],
  },
  {
    title: "7. Third-Party Services and Links",
    blocks: [
      {
        type: "p",
        text: "The website may use or link to third-party services such as appointment scheduling, video conferencing, website infrastructure, analytics, or other technology platforms.",
      },
      {
        type: "p",
        text: "iVIBES Consulting is not responsible for the availability, content, security, functionality, or privacy practices of independent third-party services.",
      },
      {
        type: "p",
        text: "Use of those services may also be governed by their own terms and policies.",
      },
    ],
  },
  {
    title: "8. Acceptable Use",
    blocks: [
      { type: "p", text: "You agree not to use this website to:" },
      {
        type: "ul",
        items: [
          "Violate applicable law",
          "Attempt unauthorized access to systems or accounts",
          "Introduce malicious software",
          "Interfere with website functionality",
          "Submit fraudulent or intentionally misleading information",
          "Abuse forms, scheduling systems, demonstrations, or communication tools",
          "Attempt to copy, reverse engineer, or misuse proprietary website functionality unlawfully",
        ],
      },
    ],
  },
  {
    title: "9. Website Availability",
    blocks: [
      {
        type: "p",
        text: "We may modify, suspend, update, or discontinue portions of the website at any time.",
      },
      {
        type: "p",
        text: "We do not guarantee uninterrupted or error-free website availability.",
      },
    ],
  },
  {
    title: "10. Disclaimer",
    blocks: [
      {
        type: "p",
        text: 'The website and its content are provided on an "as available" basis for general business information.',
      },
      {
        type: "p",
        text: "To the extent permitted by law, iVIBES Consulting disclaims warranties regarding the completeness, accuracy, uninterrupted availability, or fitness of website content for any particular purpose.",
      },
      {
        type: "p",
        text: "Nothing on this website constitutes legal, tax, accounting, financial, medical, or other regulated professional advice.",
      },
    ],
  },
  {
    title: "11. Limitation of Liability",
    blocks: [
      {
        type: "p",
        text: "To the extent permitted by applicable law, iVIBES Consulting will not be liable for indirect, incidental, special, consequential, or similar damages arising solely from the use of, inability to use, or reliance upon this website.",
      },
      {
        type: "p",
        text: "Any obligations relating to paid consulting services will be governed by the applicable written client agreement rather than these general website Terms.",
      },
    ],
  },
  {
    title: "12. Changes to These Terms",
    blocks: [
      {
        type: "p",
        text: "We may revise these Terms from time to time as the website or our business evolves.",
      },
      {
        type: "p",
        text: "The effective date at the top of this page indicates the latest revision.",
      },
      {
        type: "p",
        text: "Continued use of the website after changes are posted constitutes acceptance of the updated Terms.",
      },
    ],
  },
  {
    title: "13. Governing Law",
    blocks: [
      {
        type: "p",
        text: "These Terms will be governed by applicable United States law and, where applicable, the laws of the jurisdiction in which iVIBES Consulting is legally established, without giving effect to conflict-of-law principles.",
      },
      {
        type: "p",
        text: "Any more specific governing-law or dispute-resolution provisions applicable to a consulting engagement will appear in the corresponding client agreement.",
      },
    ],
  },
  {
    title: "14. Contact",
    blocks: [
      { type: "p", text: "Questions regarding these Terms may be sent to:" },
      { type: "p", text: "iVIBES Consulting" },
      { type: "email", text: "ismail@ivibesconsulting.com" },
    ],
  },
];

function Block({ block }) {
  if (block.type === "ul") {
    return (
      <ul className="list-disc pl-6 space-y-2 text-slate-300 leading-relaxed">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "email") {
    return (
      <p className="text-slate-300 leading-relaxed">
        Email:{" "}
        <a
          href={`mailto:${block.text}`}
          className="text-cyan-300 font-semibold hover:text-cyan-200 transition-colors duration-200"
        >
          {block.text}
        </a>
      </p>
    );
  }
  return <p className="text-slate-300 leading-relaxed">{block.text}</p>;
}

export default function TermsOfService() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#0B0F19]">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="terms-logo" className="flex items-center gap-2.5">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-7 w-auto" />
            </span>
            <span className="font-heading text-xl tracking-tight font-extrabold">
              <span className="text-cyan-400">i</span>VIBES
              <span className="text-xl font-medium text-slate-200 ml-1.5 tracking-normal">Consulting</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-cyan-300 transition-colors duration-200"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Website Terms of Service</h1>
        <p className="mt-3 text-sm text-slate-400">Effective Date: August 21, 2026</p>

        <p className="mt-8 text-slate-200 leading-relaxed">
          These Terms of Service govern your use of the iVIBES Consulting website.
        </p>
        <p className="mt-4 text-slate-200 leading-relaxed">
          By accessing or using this website, you agree to these Terms.
        </p>

        {sections.map((s) => (
          <section key={s.title} className="mt-10">
            <h2 className="font-heading font-bold text-xl text-white mb-4">{s.title}</h2>
            <div className="space-y-4">
              {s.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
